import React, { useState, useEffect } from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { FaRupeeSign, FaDownload, FaRedo } from 'react-icons/fa';
import { calculatePPF, formatCurrency } from '../utils/calculations';
import { generatePPFPDF } from '../utils/pdfGenerator';
import SEO from '../components/SEO';
import { isPrerender } from '../utils/prerender';

ChartJS.register(ArcElement, Tooltip, Legend);

const PPFCalculator = () => {
  const shouldRenderChart = !isPrerender();
  const [yearlyInvestment, setYearlyInvestment] = useState(150000);
  const [years, setYears] = useState(15);
  const [interestRate, setInterestRate] = useState(7.1);
  const [result, setResult] = useState(null);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    calculate();
  }, [yearlyInvestment, years, interestRate]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const calculate = () => {
    const y = Number(yearlyInvestment) || 0;
    const yrs = Number(years) || 0;
    const r = Number(interestRate) || 0;
    const ppfResult = calculatePPF(y, yrs, r);
    setResult(ppfResult);
  };

  const handleDownloadPDF = () => {
    generatePPFPDF({
      yearlyInvestment,
      years,
      interestRate,
      ...result,
    });
  };

  const handleReset = () => {
    setYearlyInvestment(150000);
    setYears(15);
    setInterestRate(7.1);
  };

  const chartData = result ? {
    labels: ['Total Investment', 'Total Interest'],
    datasets: [{
      data: [result.totalInvested, result.totalInterest],
      backgroundColor: ['#8b5cf6', '#10b981'],
      borderColor: ['#7c3aed', '#059669'],
      borderWidth: 2,
    }],
  } : null;

  return (
    <div className="calculator-page fade-in">
      <SEO
        title="PPF Calculator"
        description="Free PPF Calculator India - Calculate Public Provident Fund maturity amount, interest earned. Current PPF rate 7.1%. Tax-free returns under Section 80C. Export PDF."
        keywords="PPF calculator, public provident fund calculator, PPF interest rate, PPF maturity calculator, PPF investment calculator India"
        canonicalPath="/ppf"
        calculatorType="PPF Calculator"
      />
      <div className="calculator-header">
        <h1>PPF Calculator</h1>
        <p>Calculate your Public Provident Fund maturity amount</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Yearly Investment (₹)</label>
            <div className="input-with-icon">
              <FaRupeeSign className="icon" />
              <input
                type="number"
                value={yearlyInvestment === '' ? '' : yearlyInvestment}
                onChange={(e) => setYearlyInvestment(e.target.value === '' ? '' : Math.min(Number(e.target.value), 150000))}
                min="500"
                max="150000"
              />
            </div>
            <div className="slider-container">
              <input
                type="range"
                min="500"
                max="150000"
                step="500"
                value={yearlyInvestment || 0}
                onChange={(e) => setYearlyInvestment(e.target.value === '' ? '' : Number(e.target.value))}
                style={{ '--value': `${((yearlyInvestment || 0) / 150000) * 100}%` }}
              />
            </div>
            <small style={{ color: 'var(--text-secondary)' }}>
              Maximum ₹1,50,000 per year
            </small>
          </div>

          <div className="input-group">
            <label>Time Period (Years)</label>
            <input
              type="number"
              value={years === '' ? '' : years}
              onChange={(e) => setYears(e.target.value === '' ? '' : Number(e.target.value))}
              min="15"
              max="50"
            />
            <div className="slider-container">
              <input
                type="range"
                min="15"
                max="50"
                step="1"
                value={years || 0}
                onChange={(e) => setYears(e.target.value === '' ? '' : Number(e.target.value))}
                style={{ '--value': `${(((years || 0) - 15) / 35) * 100}%` }}
              />
            </div>
            <small style={{ color: 'var(--text-secondary)' }}>
              Minimum 15 years (can be extended in blocks of 5 years)
            </small>
          </div>

          <div className="input-group">
            <label>Interest Rate (% p.a.)</label>
            <input
              type="number"
              value={interestRate === '' ? '' : interestRate}
              onChange={(e) => setInterestRate(e.target.value === '' ? '' : Number(e.target.value))}
              min="1"
              max="15"
              step="0.1"
            />
            <small style={{ color: 'var(--text-secondary)' }}>
              Current PPF rate: 7.1% (as of FY 2024-25)
            </small>
          </div>

          <div className="button-group">
            <button className="btn btn-outline" onClick={handleReset}>
              <FaRedo style={{ marginRight: '8px' }} /> Reset
            </button>
            <button className="btn btn-secondary" onClick={handleDownloadPDF}>
              <FaDownload style={{ marginRight: '8px' }} /> Download PDF
            </button>
          </div>
        </div>

        <div className="calculator-results">
          {result && (
            <>
              <div className="result-summary">
                <h3>Maturity Amount</h3>
                <div className="amount">{formatCurrency(result.maturityAmount)}</div>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Total Investment</span>
                  <span className="value">{formatCurrency(result.totalInvested)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Total Interest</span>
                  <span className="value" style={{ color: '#10b981' }}>
                    +{formatCurrency(result.totalInterest)}
                  </span>
                </div>
              </div>

              {shouldRenderChart && (
                <div className="chart-container">
                  <Pie data={chartData} />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>About PPF (Public Provident Fund)</h4>
        <ul>
          <li><strong>Tax Benefit:</strong> Investments qualify for Section 80C deduction (up to ₹1.5 lakh)</li>
          <li><strong>Tax-Free Returns:</strong> Interest earned and maturity amount are tax-free (EEE status)</li>
          <li><strong>Lock-in Period:</strong> 15 years (partial withdrawal allowed from 7th year)</li>
          <li><strong>Investment Limit:</strong> ₹500 to ₹1,50,000 per year</li>
          <li><strong>Loan Facility:</strong> Available from 3rd to 6th financial year</li>
        </ul>
      </div>
    </div>
  );
};

export default PPFCalculator;
